/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { memo, useCallback, useState, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import DropdownMenu, { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import type { AddTab_container$key } from '@atlassian/jira-relay/src/__generated__/AddTab_container.graphql';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { NAVIGATION_ITEMS_PAGE_SIZE } from '@atlassian/jira-navigation-apps-resources/src/constants.tsx';
import type { TabsDescriptor } from '@atlassian/jira-horizontal-nav-common/src/common/types.tsx';
import type { JiraNavigationItemTypeKey } from '@atlassian/jira-relay/src/__generated__/AddTabContentQuery.graphql';
import {
	useOnboarding,
	ADD_EXTRA_BOARD_ONBOARDING,
} from '@atlassian/jira-business-onboarding-board-saved-views/src/controllers/onboarding-context.tsx';
import { CONTENT_MIN_WIDTH } from '../common/constants.tsx';
import AddTabTrigger from './AddTabTrigger.tsx';
import { LoadingSkeleton } from './content/loading-skeleton/index.tsx';
import { addTabContentEntryPoint } from './content/entrypoint.tsx';

type Props = {
	containerFragment: AddTab_container$key;
	tabsDescriptor: TabsDescriptor;
	onAddItem: (item: { id: string; typeKey: JiraNavigationItemTypeKey }) => void;
};

const AddTab = ({ containerFragment, tabsDescriptor, onAddItem }: Props) => {
	const containerData = useFragment(
		graphql`
			fragment AddTab_container on JiraContainerNavigationResult {
				... on JiraContainerNavigation {
					scopeId @required(action: THROW)
					# remove this addableNavigationItemTypes when deleting saved_views onboarding
					addableNavigationItemTypes(first: $first) {
						edges {
							node {
								typeKey
							}
						}
					}
				}
			}
		`,
		containerFragment,
	);

	const scopeId = containerData.scopeId ?? '';

	// delete spotlight onboarding with saved_views clean up
	const [isOnboardingEnabled, markAsSeen] = useOnboarding(ADD_EXTRA_BOARD_ONBOARDING);
	const [isOnboardingVisible, setOnboardingVisible] = useState<boolean>(false);
	const dismissOnboarding = useCallback(() => {
		setOnboardingVisible(false);
		markAsSeen();
	}, [markAsSeen]);

	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState<boolean>(false);
	const onOpenChange = useCallback(() => {
		setIsDropdownMenuOpen((isDropdownMenuCurrent) => {
			if (isOnboardingEnabled) {
				const canAddBoard = containerData.addableNavigationItemTypes?.edges?.some(
					(edge) => edge?.node?.typeKey === 'BOARD',
				);
				if (canAddBoard) {
					// when opening the dropdown, show the onboarding with a slight delay
					if (!isDropdownMenuCurrent) {
						setTimeout(() => {
							setOnboardingVisible(true);
						}, 200);
					} else {
						// do not close the dropdown if onboarding is visible
						return true;
					}
				}
			}
			return !isDropdownMenuCurrent;
		});
	}, [containerData.addableNavigationItemTypes?.edges, isOnboardingEnabled]);

	const closeMenu = useCallback(() => {
		setIsDropdownMenuOpen(false);
	}, []);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		addTabContentEntryPoint,
		useMemo(() => ({ scopeId, first: NAVIGATION_ITEMS_PAGE_SIZE }), [scopeId]),
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isDropdownMenuOpen);

	const trigger = ({ isSelected: isMenuOpen, onClick, ...triggerProps }: CustomTriggerProps) => (
		<AddTabTrigger
			onClick={(event) => {
				onClick && onClick(event);
			}}
			isSelected={isDropdownMenuOpen}
			{...triggerProps}
			ref={(element) => {
				triggerRef(element);
				typeof triggerProps.triggerRef === 'function' && triggerProps.triggerRef(element);
			}}
			testId="navigation-kit-add-tab.ui.trigger"
		/>
	);

	const runtimeProps = useMemo(
		() => ({
			onAddItem,
			onClose: closeMenu,
			tabsDescriptor,
			isOnboardingVisible,
			markAsSeen: dismissOnboarding,
		}),
		[closeMenu, isOnboardingVisible, dismissOnboarding, onAddItem, tabsDescriptor],
	);

	return (
		<DropdownMenu
			trigger={trigger}
			placement="bottom-start"
			testId="navigation-kit-add-tab.ui.dropdown-menu"
			isOpen={isDropdownMenuOpen}
			onOpenChange={onOpenChange}
		>
			<Box xcss={addTabContentWrapperStyles}>
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="navigation-kit-add-tab-content"
					packageName="jiraNavigationKitAddTab"
					runtimeProps={runtimeProps}
					fallback={<LoadingSkeleton />}
				/>
			</Box>
		</DropdownMenu>
	);
};

export default memo(AddTab);

const addTabContentWrapperStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	inlineSize: `${CONTENT_MIN_WIDTH}px`,
});
